import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WhiteboardingService } from '../../../../core/services/wrench-services/whiteboarding.service';
import { GlobeService } from '../../../../core/services/globe.service';
import * as Cesium from 'cesium';

@Component({
  selector: 'app-whiteboarding-palette',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './whiteboarding-palette.component.html',
  styleUrl: './whiteboarding-palette.component.css'
})
export class WhiteboardingPaletteComponent implements OnInit, OnDestroy {
  constructor(
    public activeModal: NgbActiveModal,
    private whiteboardingService: WhiteboardingService,
    private globeService: GlobeService
  ) {}

  selectedWidth: number = 2;
  brushSize = 5;
  private viewer: any;
  private handler: any;
  private drawing = false;
  private positions: Cesium.Cartesian3[] = [];
  private currentPolyline: any;
  private lastPosition: Cesium.Cartesian3 | null = null;

  colors: string[] = [
    '#ff3b30', // red
    '#ffcc00', // yellow
    '#4cd964', // green
    '#5ac8fa', // blue
    '#af52de', // purple
    '#ff2d55', // pink
    '#1c1c1e', // black
    '#ffffff'  // white
  ];

  selectedColor = this.colors[0];

  ngOnInit(): void {
    this.viewer = this.globeService.getViewer();
    if (!this.viewer) {
      return;
    }
    this.whiteboardingService.setViewer(this.viewer);
    const controller = this.viewer.scene.screenSpaceCameraController;
    controller.enableRotate = false;
    controller.enableTranslate = false;
    controller.enableZoom = false;
    controller.enableTilt = false;
    controller.enableLook = false;

    this.setupGlobeClickHandler();
  }

  ngOnDestroy(): void {
    if (this.viewer && this.viewer.scene && this.viewer.scene.screenSpaceCameraController) {
      const controller = this.viewer.scene.screenSpaceCameraController;
      controller.enableRotate = true;
      controller.enableTranslate = true;
      controller.enableZoom = true;
      controller.enableTilt = true;
      controller.enableLook = true;
    }
    this.cleanup();
  }

  private setupGlobeClickHandler(): void {
    this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);

    this.handler.setInputAction((event: any) => {
      const position = this.viewer.camera.pickEllipsoid(event.position);
      if (position) {
        this.startDrawing(position);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    this.handler.setInputAction((event: any) => {
      if (this.drawing) {
        this.endDrawing();
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    this.handler.setInputAction((event: any) => {
      if (this.drawing) {
        const position = this.viewer.camera.pickEllipsoid(event.endPosition);
        if (position) {
          this.continueDrawing(position);
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  }

  private startDrawing(position: Cesium.Cartesian3): void {
    this.drawing = true;
    this.positions = [position];
    this.lastPosition = position;
    this.currentPolyline = this.viewer.entities.add({
      polyline: {
        positions: this.positions,
        width: this.brushSize,
        material: new Cesium.ColorMaterialProperty(
          Cesium.Color.fromCssColorString(this.selectedColor)
        ),
        clampToGround: true
      }
    });

    this.whiteboardingService.addDrawing(this.currentPolyline);
  }

  private continueDrawing(position: Cesium.Cartesian3): void {
    if (!this.drawing || !this.lastPosition) return;
  
    this.positions.push(position);
    
    if (this.currentPolyline) {
      this.currentPolyline.polyline.positions = this.positions;
    }
    
    this.lastPosition = position;
  }

  private endDrawing(): void {
    if (!this.drawing) return;
    
    this.drawing = false;
    this.lastPosition = null;
    this.positions = [];
    this.currentPolyline = undefined;
  }

  selectColor(color: string): void {
    this.selectedColor = color;
    this.whiteboardingService.setColor(color);

    // If currently drawing, update the polyline's color immediately
    if (this.currentPolyline && this.drawing) {
      this.currentPolyline.polyline.material = new Cesium.ColorMaterialProperty(
        Cesium.Color.fromCssColorString(color)
      );
    }
  }

  setWidth(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.brushSize = parseInt(input.value, 10);
    this.whiteboardingService.setWidth(this.brushSize);
    
    if (this.currentPolyline) {
      this.currentPolyline.polyline.width = this.brushSize;
    }
  }

  undo(): void {
    this.whiteboardingService.undo();
  }

  clear(): void {
    this.whiteboardingService.clearCanvas();
  }

  private cleanup(): void {
    if (this.handler) {
      this.handler.destroy();
    }
    this.endDrawing();
  }

  close(): void {
    this.clear();
    this.cleanup();
    this.activeModal.dismiss();
  }
}
